import React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@core/components/ui/dialog";
import { useQuotaModal } from "@core/contexts/QuotaModalContext";
import { useCheckout } from "@core/hooks/useCheckout";
import { PlanId } from "@core/constants/quotas";
import { useAuth } from "@core/contexts/AuthContext";
import { AuthModal } from "./AuthModal";
import { WithdrawalWaiverCheckbox } from "@core/components/billing/WithdrawalWaiverCheckbox";
import { Rocket, Sparkles, X } from "lucide-react";

export function QuotaLimitModal() {
  const { isOpen, modalType, productHint, closeQuotaModal } = useQuotaModal();
  const { handleUpgrade, loading, needsAuth, dismissAuth } = useCheckout({
    returnPath: window.location.pathname,
  });
  const { user } = useAuth();

  // Helper to get contextual plan info
  const getContextualPlan = () => {
    switch (productHint) {
      case "quizz":
        return { id: "starter_quizz", name: "Quizz", price: "4.99€" };
      case "date":
        return { id: "starter_date", name: "Date", price: "2.99€" };
      case "availability":
        return {
          id: "premium_availability",
          name: "Availability",
          price: "6.95€",
        };
      case "form":
        return { id: "dooquest_pro", name: "DooQuest Pro", price: "70€/an" };
      default:
        return null;
    }
  };

  const contextualPlan = getContextualPlan();

  // State for nested auth modal if user clicks "Sign in"
  const [authModalOpen, setAuthModalOpen] = React.useState(false);
  const [waiverAccepted, setWaiverAccepted] = React.useState(false);
  const [authMode, setAuthMode] = React.useState<"signin" | "signup">("signup");

  const handleAuthAction = (mode: "signin" | "signup") => {
    setAuthMode(mode);
    setAuthModalOpen(true);
  };

  // Invité qui clique « Passer à Solo » : useCheckout demande la connexion
  // d'abord (#1470) — on ouvre la modale de connexion au lieu de ne rien faire.
  React.useEffect(() => {
    if (needsAuth) {
      setAuthMode("signin");
      setAuthModalOpen(true);
      dismissAuth?.();
    }
  }, [needsAuth, dismissAuth]);

  if (!isOpen) return null;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={closeQuotaModal}>
        <DialogContent className="sm:max-w-md bg-card border border-border text-foreground">
          <DialogHeader>
            <div className="mx-auto bg-primary/20 w-16 h-16 rounded-full flex items-center justify-center mb-4">
              {modalType === "plan_limit" ? (
                <Rocket className="w-8 h-8 text-primary" />
              ) : (
                <Sparkles className="w-8 h-8 text-primary" />
              )}
            </div>
            <DialogTitle className="text-center text-xl font-bold">
              {modalType === "plan_limit"
                ? "Passez à la vitesse supérieure 🚀"
                : "Vous avez atteint la limite gratuite ✨"}
            </DialogTitle>
            <DialogDescription className="text-center text-muted-foreground pt-2">
              {modalType === "plan_limit"
                ? "Débloquez plus de fonctionnalités et supprimez les limites sur vos sondages."
                : "Créez un compte gratuit pour obtenir plus de crédits et sauvegarder votre travail."}
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-3 mt-6">
            {modalType === "plan_limit" ? (
              <>
                {contextualPlan && (
                  <button
                    onClick={() =>
                      handleUpgrade(contextualPlan.id as unknown as PlanId)
                    }
                    disabled={loading}
                    className="w-full py-3 px-4 bg-success hover:bg-success/90 text-success-foreground font-semibold rounded-lg shadow-lg hover:shadow-success/25 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="quotalimitmodal-passer-au-pack"
                  >
                    Passer au Pack {contextualPlan.name} ({contextualPlan.price}
                    /mois)
                  </button>
                )}

                {/* Renonciation au droit de rétractation (art. L221-28 13°
                    C. consom.) — même case et même blocage que la page Tarifs */}
                <WithdrawalWaiverCheckbox
                  checked={waiverAccepted}
                  onChange={setWaiverAccepted}
                  testId="quota-withdrawal-waiver"
                />
                <button
                  onClick={() =>
                    handleUpgrade("SOLO", {
                      waiverAcceptedAt: new Date().toISOString(),
                    })
                  }
                  disabled={loading || !waiverAccepted}
                  aria-disabled={loading || !waiverAccepted}
                  className="w-full py-3 px-4 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold rounded-lg shadow-lg hover:shadow-primary/25 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="quota-upgrade-btn"
                >
                  {loading ? "Chargement..." : "Passer à Solo (30€/an)"}
                </button>
                <button
                  onClick={closeQuotaModal}
                  className="w-full py-2.5 px-4 bg-transparent hover:bg-accent text-muted-foreground hover:text-foreground rounded-lg font-medium transition-colors"
                  data-testid="quotalimitmodal-peuttre-plus-tard"
                >
                  Peut-être plus tard
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => handleAuthAction("signup")}
                  className="w-full py-3 px-4 bg-success hover:bg-success/90 text-success-foreground font-semibold rounded-lg shadow-lg hover:shadow-success/25 transition-all flex items-center justify-center gap-2"
                  data-testid="quota-auth-signup-btn"
                >
                  Créer un compte gratuit
                </button>
                <div className="text-center">
                  <span className="text-sm text-muted-foreground">
                    Déjà un compte ?{" "}
                  </span>
                  <button
                    onClick={() => handleAuthAction("signin")}
                    className="text-sm text-success hover:underline font-medium"
                    data-testid="quota-auth-signin-btn"
                  >
                    Se connecter
                  </button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Nested Auth Modal */}
      <AuthModal
        open={authModalOpen}
        onOpenChange={(open) => {
          setAuthModalOpen(open);
          if (!open && user) {
            // If closed and user is logged in, assume success and close quota modal too
            closeQuotaModal();
          }
        }}
        defaultMode={authMode}
      />
    </>
  );
}
